/**
 * ============================================================================
 * EmbedNodeView — NodeView React do bloco `embed` (iframe YouTube/Loom/Figma).
 * Mostra placeholder quando não há URL e permite trocar a URL depois.
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-08 v1.0.0 - Versão inicial.
 * ============================================================================
 */
import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import { useState } from "react";
import { Youtube, Pencil, Trash2 } from "lucide-react";

// Normaliza URL para embed (mesma lógica do toolbar/slash menu)
function normalizeEmbed(url: string): { src: string; provider: string } {
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]+)/);
  if (yt) return { src: `https://www.youtube.com/embed/${yt[1]}`, provider: "youtube" };
  if (url.includes("loom.com/share/")) return { src: url.replace("/share/", "/embed/"), provider: "loom" };
  if (url.includes("figma.com")) return { src: `https://www.figma.com/embed?embed_host=share&url=${encodeURIComponent(url)}`, provider: "figma" };
  return { src: url, provider: "generic" };
}

export function EmbedNodeView({ node, updateAttributes, deleteNode, selected, editor }: NodeViewProps) {
  const src = String(node.attrs.src ?? "");
  const [editing, setEditing] = useState(!src);
  const [draft, setDraft] = useState(src);
  const editable = editor?.isEditable ?? true;

  const save = () => {
    if (!draft.trim()) return;
    const { src: normalized, provider } = normalizeEmbed(draft.trim());
    updateAttributes({ src: normalized, provider });
    setEditing(false);
  };

  return (
    <NodeViewWrapper
      className={`kb-embed-wrapper relative ${selected ? "ring-2 ring-primary rounded-md" : ""}`}
    >
      {src && !editing && (
        <iframe
          src={src}
          className="kb-embed"
          allow="fullscreen"
          data-embed="1"
          title="Embed"
        />
      )}

      {(!src || editing) && (
        <div className="flex flex-col items-stretch gap-2 border border-dashed rounded-md p-4 bg-muted/30">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Youtube className="h-4 w-4" /> Embed (YouTube, Loom, Figma, PDF)
          </div>
          <div className="flex items-center gap-2">
            <input
              autoFocus
              className="flex-1 text-sm px-2 py-1.5 bg-background border rounded outline-none"
              placeholder="Cole a URL aqui…"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") save();
                if (e.key === "Escape" && src) setEditing(false);
              }}
            />
            <button
              type="button"
              className="text-sm px-3 py-1.5 rounded bg-primary text-primary-foreground"
              onClick={save}
            >
              Inserir
            </button>
          </div>
        </div>
      )}

      {editable && src && !editing && (
        <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 hover:opacity-100 transition-opacity bg-background/90 backdrop-blur border rounded-md shadow p-0.5">
          <button
            type="button"
            title="Trocar URL"
            className="p-1 rounded hover:bg-accent"
            onClick={() => { setDraft(src); setEditing(true); }}
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            title="Remover"
            className="p-1 rounded hover:bg-destructive/10 text-destructive"
            onClick={() => deleteNode()}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </NodeViewWrapper>
  );
}
